import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';

import { MasterBaselines } from './MasterBaselines';
import { MasterBaselineReview } from './MasterBaselineReview';

/**
 * Baselines — the superadmin master-baseline surfaces (the curated catalogue
 * + the tenant-submission review queue) consolidated from two pages into one
 * tabbed page. Each panel is the original page component in `embedded` mode
 * (its own h4/description suppressed; page-level controls kept). The active
 * tab lives in the URL (`?tab=master|submissions`) so the old routes
 * deep-link straight to the right tab.
 */
const TABS = [
  {
    key: 'master',
    label: 'Catalogue',
    render: (slot: HTMLElement | null) => (
      <MasterBaselines embedded actionsSlot={slot} />
    ),
  },
  {
    key: 'submissions',
    label: 'Submissions',
    render: (slot: HTMLElement | null) => (
      <MasterBaselineReview embedded actionsSlot={slot} />
    ),
  },
] as const;

export function Baselines() {
  const [params, setParams] = useSearchParams();
  // Host for the active tab's page-level controls (view toggle, Refresh,
  // Import, Add row / the submissions status filter), portaled onto the
  // tab line — same pattern as tenant Settings and Access.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const requested = params.get('tab');
  const active = Math.max(
    0,
    TABS.findIndex((t) => t.key === requested),
  );

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h4">Clinical Reference Library</Typography>
        <Typography variant="body2" color="text.secondary">
          The curated catalogue of validated reference values and the
          tenant-submission review queue.
        </Typography>
      </Stack>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={active}
          onChange={(_e, v: number) =>
            setParams({ tab: TABS[v].key }, { replace: true })
          }
          aria-label="Baselines sections"
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box
          ref={setActionsSlot}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}
        />
      </Box>

      <Box>{TABS[active].render(actionsSlot)}</Box>
    </Stack>
  );
}
